(() => {
  /**
   * @typedef {{handleClick: (event: {target: Element}) => void, handleChange: (event: {target: Element}) => void}} ProductControllerApi
   */
  /** @typedef {HTMLElement & {__tuckyProductController?: ProductControllerApi}} ProductSection */

  /** @type {WeakMap<ProductSection, Promise<ProductControllerApi|undefined>>} */
  const pending = new WeakMap();
  const controls = '[data-pdp-thumbnail], [data-pdp-media-step], [data-pdp-option-choice], [data-pdp-quantity-step]';

  /** @param {ProductSection} root @returns {Promise<ProductControllerApi|undefined>} */
  const loadController = (root) => {
    if (root.__tuckyProductController) return Promise.resolve(root.__tuckyProductController);
    if (!pending.has(root)) {
      const interactionSrc = root.dataset.interactionSrc;
      if (!interactionSrc) return Promise.resolve(undefined);
      const interactionUrl = new URL(interactionSrc, document.baseURI).href;
      const loading = import(interactionUrl)
        .then(async () => {
          if (document.readyState === 'loading') {
            /** @type {Promise<void>} */
            const documentReady = new Promise((resolve) =>
              document.addEventListener('DOMContentLoaded', () => resolve(), { once: true }),
            );
            await documentReady;
          }
          return root.__tuckyProductController;
        })
        .catch((/** @type {unknown} */ error) => {
          pending.delete(root);
          throw error;
        });
      pending.set(root, loading);
    }
    return pending.get(root) ?? Promise.resolve(undefined);
  };

  /** @param {Event} event @param {Element} control @param {ProductSection} root @param {boolean} [suppressOriginal] */
  const enhanceInteraction = (event, control, root, suppressOriginal = event.type === 'click') => {
    if (suppressOriginal) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
    loadController(root)
      .then((controller) => {
        if (!controller || !control.isConnected) return;
        const replayedEvent = { target: control };
        if (event.type === 'click') controller.handleClick(replayedEvent);
        else controller.handleChange(replayedEvent);
      })
      .catch(reportInitializationError);
  };

  /** @param {unknown} error */
  const reportInitializationError = (error) => {
    console.error('Product controls could not be initialized.', error);
  };

  /** @param {Document|Element} [scope] */
  const initializeVariantSections = (scope = document) => {
    /** @type {ProductSection[]} */
    const sections = [];
    if (scope instanceof Element && scope.matches('.product-main[data-section-id]'))
      sections.push(/** @type {ProductSection} */ (scope));
    scope
      .querySelectorAll('.product-main[data-section-id]')
      .forEach((section) => sections.push(/** @type {ProductSection} */ (section)));

    sections.forEach((section) => {
      const usesEnhancedVariants = section.dataset.productStyle === 'cuppy' || section.dataset.pdpLayout === 'shared';
      if (!usesEnhancedVariants || !section.querySelector('[data-pdp-variant-select]')) return;
      loadController(section).catch(reportInitializationError);
    });
  };

  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;
      const control = event.target.closest(controls);
      const rootElement = control?.closest('.product-main[data-section-id]');
      const root = rootElement instanceof HTMLElement ? /** @type {ProductSection} */ (rootElement) : null;
      if (control && root && !root.__tuckyProductController) enhanceInteraction(event, control, root);
    },
    true,
  );

  document.addEventListener(
    'change',
    (event) => {
      if (!(event.target instanceof Element)) return;
      if (!event.target.matches('[data-pdp-variant-select], [data-pdp-quantity]')) return;
      const rootElement = event.target.closest('.product-main[data-section-id]');
      const root = rootElement instanceof HTMLElement ? /** @type {ProductSection} */ (rootElement) : null;
      if (root && !root.__tuckyProductController) enhanceInteraction(event, event.target, root, false);
    },
    true,
  );

  initializeVariantSections();
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element) initializeVariantSections(event.target);
  });
})();
